import moment from "moment";
import { Fetch } from "./fetch";
import jwt from "./jwt";
import { getEffectiveUserType } from "./consentUtils";
import { operatorType } from "../config";

export const AGREEMENT_TRACKING_PATH = "/agreement-tracking";

export const ENTITY_TYPE_LABELS = {
  CONTRACT: "MG Contract",
  EV_SERVICE_CONTRACT: "EV Service Contract",
  EV_LEASE_CONTRACT: "EV Lease Contract",
  ADDENDUM: "Addendum",
};

export const APPROVAL_STATUS = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
};

// Values the respond API expects in `action`.
export const RESPOND_ACTION = {
  ACCEPT: "APPROVE",
  REJECT: "REJECT",
};

export const REJECTION_REMARKS_MAX_LENGTH = 350;

const MODAL_DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000;

// Agreement tracking only exists for the MG and ELECTRIC business models.
export const getBusinessModel = () => {
  const decodedToken = jwt.decode(localStorage.getItem("idToken"));
  if (!decodedToken) return null;
  const userType = getEffectiveUserType(decodedToken);
  if (userType === operatorType.BUSOWNER) return "MG";
  if (userType === "ELECTRIC") return "ELECTRIC";
  return null;
};

// Logged-in operator's name from the token (the agreementMapping API only
// returns operator ids).
export const getOperatorName = () => {
  const decodedToken = jwt.decode(localStorage.getItem("idToken"));
  if (!decodedToken) return null;
  if (getBusinessModel() === "ELECTRIC") return decodedToken.electricName;
  return decodedToken.mgName || decodedToken.name;
};

// Deep links (e.g. the reminder SMS/WhatsApp) land on MG by default, and on
// ELECTRIC only when the operator has no MG access.
export const ensureAgreementBusinessModel = () => {
  if (getBusinessModel()) return;
  const decodedToken = jwt.decode(localStorage.getItem("idToken"));
  if (!decodedToken) return;
  if (decodedToken.mgId || decodedToken.userType === operatorType.BUSOWNER) {
    sessionStorage.setItem("userType", operatorType.BUSOWNER);
  } else if (decodedToken.electricId || decodedToken.isElectricUser) {
    sessionStorage.setItem("userType", "ELECTRIC");
  }
};

export const formatValidity = (from, till) => {
  const format = (date) => (date ? moment(date).format("D/M/YY") : "");
  if (!from && !till) return "-";
  return `${format(from)} - ${format(till)}`;
};

export const formatActionTakenOn = (date) =>
  date ? moment(date).format("h:mm A, D MMMM YYYY") : "-";

export const formatDate = (date) =>
  date ? moment(date).format("D MMM YYYY") : null;

const isAddendum = (item) => item.entityType === "ADDENDUM";

// Route as "From - To": city names when both are set, else the contract's
// route string (stored as "Delhi_Mumbai").
export const formatRoute = (details) => {
  const fromCity = details.fromCity && details.fromCity.name;
  const toCity = details.toCity && details.toCity.name;
  if (fromCity && toCity) return `${fromCity} - ${toCity}`;
  return details.route ? details.route.split("_").join(" - ") : null;
};

const normalizeItem = (raw, agreement) => ({
  id: raw._id,
  entityType: raw.entityType,
  typeLabel: ENTITY_TYPE_LABELS[raw.entityType] || raw.entityLabel,
  name: raw.name,
  // EV lease contracts carry startDate/endDate instead of effective dates.
  validFrom: raw.effectiveFromDate || raw.startDate,
  validTill: raw.effectiveTillDate || raw.endDate,
  // Toggled Inactive on Zupiter's agreement mapping screen.
  isActive: !raw.isArchive,
  approvalStatus: raw.operatorActionStatus,
  actionTakenOn: raw.approvedOn || raw.rejectedOn,
  details: raw,
  // Shown on the contract details view.
  agreement: {
    name: agreement.name,
    operatorName:
      agreement.operatorEsignDetails && agreement.operatorEsignDetails.name,
  },
});

const normalizeAgreement = ({ agreement, items = [] }) => {
  const normalizedItems = items.map((item) => normalizeItem(item, agreement));
  const { agreementStartDate, agreementEndDate } =
    agreement.defaultVariables || {};
  return {
    id: agreement._id,
    name: agreement.name,
    validFrom: agreementStartDate,
    validTo: agreementEndDate,
    // Contracts first, then addendums; the API's order (latest last) is kept
    // within each group.
    items: [
      ...normalizedItems.filter((item) => !isAddendum(item)),
      ...normalizedItems.filter(isAddendum),
    ],
  };
};

// Maps the agreementMapping API response ([{ agreement, items }]) to the shape
// the UI uses.
export const normalizeAgreements = (response) =>
  Array.isArray(response) ? response.map(normalizeAgreement) : [];

export const fetchAgreementMappings = async () =>
  normalizeAgreements(await Fetch(`/user/agreementMapping`));

export const fetchPendingAgreementMappings = async () =>
  normalizeAgreements(await Fetch(`/user/agreementMapping/pending`))
    // Inactive items never appear in the login modal.
    .map((agreement) => ({
      ...agreement,
      items: agreement.items.filter((item) => item.isActive),
    }))
    .filter((agreement) => agreement.items.length > 0);

export const respondToAgreementItem = async (item, action, rejectionRemarks) => {
  const data = { entityType: item.entityType, action };
  if (rejectionRemarks) {
    data.rejectionRemarks = rejectionRemarks;
  }
  const response = await Fetch(`/user/agreementMapping/respond/${item.id}`, {
    method: "put",
    data,
  });
  return Boolean(response && response.success);
};

export const markAgreementItemsViewed = (items) =>
  Fetch(`/user/agreementMapping/markViewed`, {
    method: "put",
    data: {
      items: items.map((item) => ({
        entityId: item.id,
        entityType: item.entityType,
      })),
    },
  });

// URL endpoints return the link as a string (or { url }); errors come back as
// non-URL strings.
const toPdfUrl = (response) => {
  const url = response && typeof response === "object" ? response.url : response;
  return typeof url === "string" && url.startsWith("http") ? url : null;
};

export const fetchAgreementPdfUrl = async (agreementId) =>
  toPdfUrl(await Fetch(`/user/signedAgreement/${agreementId}`));

export const fetchAddendumPdfUrl = async (addendumId) =>
  toPdfUrl(await Fetch(`/user/agreementMapping/addendumPdf/${addendumId}`));

// MG and electric users carry their id as mgId / electricId (same as the
// userId header in fetch.js), not as id
const dismissKey = () => {
  const decodedToken = jwt.decode(localStorage.getItem("idToken"));
  const businessModel = getBusinessModel();
  let userId = "";
  if (decodedToken) {
    if (businessModel === "ELECTRIC") {
      userId = decodedToken.electricId || decodedToken.id || "";
    } else if (businessModel === "MG") {
      userId = decodedToken.mgId || decodedToken.id || "";
    } else {
      userId = decodedToken.id || "";
    }
  }
  return `trackAgreementsDismissedAt_${userId}_${businessModel}`;
};

export const isTrackAgreementsModalSnoozed = () => {
  const dismissedAt = parseInt(localStorage.getItem(dismissKey()), 10);
  return Boolean(dismissedAt) && Date.now() - dismissedAt < MODAL_DISMISS_WINDOW_MS;
};

export const snoozeTrackAgreementsModal = () => {
  localStorage.setItem(dismissKey(), Date.now().toString());
};
